import { RoadVehicleType } from '@lune-climate/lune'
import {
    EmissionEstimationIcons,
    getTradeLaneLabel,
    getTransitCorridorLabel,
} from '@lune-fe/lune-components-lib'
import { Text } from '@lune-fe/lune-ui-lib'
import { Box, Stack } from '@mui/material'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import { assertType } from 'utils'
import AdditionalPropertiesSelector from 'views/CalculateEmissions/RFQ/components/AdditionalPropertiesSelector'
import GroupedParameterMapping from 'views/CalculateEmissions/RFQ/components/GroupedParameterMapping'
import Labels from 'views/CalculateEmissions/RFQ/components/Labels'
import ParameterMapping, {
    decodeTransportModeParameter,
    ExtendedParameterName,
} from 'views/CalculateEmissions/RFQ/components/ParameterMapping'
import { transportModeToGroupMapping } from 'views/CalculateEmissions/RFQ/components/TransportModeGroupSelect'
import { TransportModeProps } from 'views/CalculateEmissions/RFQ/components/TransportModePicker'
import {
    AircraftType,
    complexRoadTransportModeArray,
    ParameterName,
    parseParameterName,
    parseTransportMode,
    PropertyMapping,
    RoadFuelType,
    RowNumber,
    simpleTransportModes,
    Tradelane,
    TransitCorridor,
    TransportMode,
    TransportModeGroup,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    DisplayProperty,
    PARAMETER_MAPPING_OUTPUT,
    TRANSPORT_MODES_PER_GROUP_PROPERTY,
    TransportModeProperty,
} from 'views/CalculateEmissions/RFQ/utils/types'
import {
    cleanDisplayProperties,
    generatePerTransportModeRowIndexes,
    generateTransportModeMapping,
    getGroupParameterTransportModes,
    getRoadFuelLabel,
    getRoadVehicleLabel,
    objectHasKey,
    propertiesToDisplayInMapping,
    uniqObj,
} from 'views/CalculateEmissions/RFQ/utils/utils'

export const groupIconMap: Record<TransportModeGroup, keyof typeof EmissionEstimationIcons> = {
    Sea: 'ship',
    Air: 'plane',
    Road: 'truck',
    Rail: 'train',
    'Inland waterways': 'waterway',
}

// eslint-disable-next-line complexity
const TransportModeParameters = ({
    state,
    transportModeMapping,
    onUpdate,
    modeMappingError,
    autoFill,
    selectedGroup,
}: TransportModeProps) => {
    const [perTransportModeRowIndexes, setPerTransportModeRowIndexes] = useState<{
        [K in TransportMode]?: number[]
    }>({})
    const [selectedIds, setSelectedIds] = useState<DisplayProperty[]>([])

    const updateGroupedTransportModeParameters = (
        transportModes: TransportMode[],
        parameter: ParameterName,
        mapping: PropertyMapping<unknown> | undefined,
    ) => {
        const newMapping = {
            ...transportModeMapping!,
            data: transportModeMapping?.data ?? {},
        }

        for (const transportMode of transportModes) {
            newMapping.data[transportMode] = {
                ...(newMapping.data[transportMode] ?? {}),
                [parameter]: mapping,
            }
        }

        onUpdate(newMapping)
    }

    const updateTransportModeParameters = (
        parameterName: ExtendedParameterName,
        mapping: PropertyMapping<unknown> | undefined,
    ) => {
        const newMapping = {
            ...transportModeMapping!,
        }

        // The type of the parameter isn't precise enough, we have to assert to be able
        // to assign below, hence the type assertions.
        // We depend on the function to be called with the correct parameterName + mapping
        // combination.
        if (parameterName === 'transportMode') {
            if (mapping === undefined) {
                return onUpdate({ mode: undefined })
            }
            if (newMapping.mode?.source === 'constant') {
                throw new Error('Expected to be in column mode here but found constant')
            }

            // Transport mode mapping was updated: use previous mapping as base to generate
            // the new mapping, allowing existing property mappings to be preserverd.
            const typedMappingMode = mapping as PropertyMapping<TransportMode>
            const { mappingData, perTransportModeRowIndexes } = generateTransportModeMapping({
                state,
                previousTransportModeMapping: transportModeMapping,
                newTransportModeMapping: typedMappingMode,
                autoFill,
                options,
            })
            newMapping.mode = typedMappingMode
            newMapping.data = mappingData
            setPerTransportModeRowIndexes(perTransportModeRowIndexes)
        } else {
            const data = decodeTransportModeParameter(parameterName)
            const mode = parseTransportMode(data.transportMode).unwrap()
            const parameter = parseParameterName(data.parameter).unwrap()
            newMapping.data = {
                ...newMapping.data,
                [mode]: { ...newMapping.data?.[mode], [parameter]: mapping },
            }
        }
        onUpdate(newMapping)
    }

    useEffect(() => {
        if (transportModeMapping?.mode) {
            setPerTransportModeRowIndexes(
                generatePerTransportModeRowIndexes(
                    state.sheet,
                    RowNumber.fromZeroBased(state.headerRow),
                    RowNumber.fromZeroBased(state.endRow),
                    transportModeMapping.mode ?? {},
                ),
            )
        }
    }, [transportModeMapping?.mode])

    const buildRefrigerationParameter = (
        transportMode: (typeof complexRoadTransportModeArray)[number] | 'Container Ship',
    ) => (
        <ParameterMapping<boolean>
            key={`${transportMode}_refrigeration-parameter`}
            state={state}
            parameter={`${transportMode}_refrigeration`}
            useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
            mappingType="mapping"
            outputValues={PARAMETER_MAPPING_OUTPUT.refrigeration}
            formatOutput={String}
            propertyMapping={
                transportModeMapping?.data?.[transportMode]
                    ? transportModeMapping.data[transportMode].refrigeration
                    : undefined
            }
            onUpdate={updateTransportModeParameters}
            autoFill={autoFill[transportMode]?.refrigeration}
            legNumber={state.currentLeg}
            allowedRowIndexes={perTransportModeRowIndexes[transportMode]}
        />
    )

    const truckParameters = (
        truckType:
            | 'Inferred truck'
            | 'Truck'
            | 'Truck rigid 7.5t'
            | 'Truck rigid 12t'
            | 'Truck rigid 20t'
            | 'Truck rigid 26t'
            | 'Truck rigid 32t'
            | 'Truck articulated 34t'
            | 'Truck articulated 40t'
            | 'Truck articulated 44t'
            | 'Truck articulated 60t'
            | 'Truck articulated 72t',
    ) => ({
        refrigeration: buildRefrigerationParameter(truckType),
        fuel: (
            <ParameterMapping<RoadFuelType>
                key={`${truckType}_fuel-parameter`}
                parameter={`${truckType}_fuel`}
                state={state}
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="mapping"
                outputValues={PARAMETER_MAPPING_OUTPUT.fuel}
                formatOutput={getRoadFuelLabel}
                propertyMapping={
                    transportModeMapping?.data?.[truckType]?.fuel
                        ? transportModeMapping.data[truckType].fuel
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill[truckType]?.fuel}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes[truckType]}
            />
        ),
    })

    const containerShipParameters = {
        refrigeration: buildRefrigerationParameter('Container Ship'),
        tradelane: (
            <ParameterMapping<Tradelane>
                key="Container Ship_tradelane-parameter"
                isTradelane
                state={state}
                parameter="Container Ship_tradelane"
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="mapping"
                outputValues={PARAMETER_MAPPING_OUTPUT.tradelane}
                formatOutput={(val) => getTradeLaneLabel(val)}
                propertyMapping={
                    transportModeMapping?.data?.['Container Ship']
                        ? transportModeMapping.data['Container Ship'].tradelane
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill['Container Ship']?.tradelane}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes['Container Ship']}
            />
        ),
        vesselImo: (
            <ParameterMapping<string>
                key="Container Ship_vesselImo-parameter"
                state={state}
                parameter="Container Ship_vesselImo"
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="direct"
                outputValues={PARAMETER_MAPPING_OUTPUT.vesselImo}
                formatOutput={String}
                propertyMapping={
                    transportModeMapping?.data?.['Container Ship']
                        ? transportModeMapping.data['Container Ship'].vesselImo
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill['Container Ship']?.vesselImo}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes['Container Ship']}
            />
        ),
        vesselName: (
            <ParameterMapping<string>
                key="Container Ship_vesselName-parameter"
                state={state}
                parameter="Container Ship_vesselName"
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="direct"
                outputValues={PARAMETER_MAPPING_OUTPUT.vesselName}
                formatOutput={String}
                propertyMapping={
                    transportModeMapping?.data?.['Container Ship']
                        ? transportModeMapping.data['Container Ship'].vesselName
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill['Container Ship']?.vesselName}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes['Container Ship']}
            />
        ),
        departureOn: (
            <ParameterMapping<string>
                key="Container Ship_departureOn-parameter"
                state={state}
                parameter={`Container Ship_departureOn`}
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="direct"
                outputValues={PARAMETER_MAPPING_OUTPUT.departureOn}
                formatOutput={String}
                propertyMapping={
                    transportModeMapping?.data?.['Container Ship']
                        ? transportModeMapping.data['Container Ship'].departureOn
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill['Container Ship']?.departureOn}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes['Container Ship']}
            />
        ),
        arrivalOn: (
            <ParameterMapping<string>
                key="Container Ship_arrivalOn-parameter"
                state={state}
                parameter={`Container Ship_arrivalOn`}
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="direct"
                outputValues={PARAMETER_MAPPING_OUTPUT.arrivalOn}
                formatOutput={String}
                propertyMapping={
                    transportModeMapping?.data?.['Container Ship']
                        ? transportModeMapping.data['Container Ship'].arrivalOn
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill['Container Ship']?.arrivalOn}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes['Container Ship']}
            />
        ),
        transitCorridor: (
            <ParameterMapping<TransitCorridor>
                key="Container Ship_transitCorridor-parameter"
                state={state}
                parameter="Container Ship_transitCorridor"
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="mapping"
                outputValues={PARAMETER_MAPPING_OUTPUT.transitCorridor}
                formatOutput={(val) => getTransitCorridorLabel(val)}
                propertyMapping={
                    transportModeMapping?.data?.['Container Ship']
                        ? transportModeMapping.data['Container Ship'].transitCorridor
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill['Container Ship']?.transitCorridor}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes['Container Ship']}
            />
        ),
        restrictedTransitCorridor: (
            <ParameterMapping<TransitCorridor>
                key="Container Ship_restrictedTransitCorridor-parameter"
                state={state}
                parameter="Container Ship_restrictedTransitCorridor"
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="mapping"
                outputValues={PARAMETER_MAPPING_OUTPUT.restrictedTransitCorridor}
                formatOutput={(val) => getTransitCorridorLabel(val)}
                propertyMapping={
                    transportModeMapping?.data?.['Container Ship']
                        ? transportModeMapping.data['Container Ship'].restrictedTransitCorridor
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill['Container Ship']?.restrictedTransitCorridor}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes['Container Ship']}
            />
        ),
    }

    const planeParameters = (planeType: 'Passenger Plane' | 'Cargo Plane' | 'Plane') => ({
        aircraftType: (
            <ParameterMapping<AircraftType>
                key={`${planeType}_aircraftType-parameter`}
                state={state}
                parameter={`${planeType}_aircraftType`}
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="mapping"
                outputValues={PARAMETER_MAPPING_OUTPUT.aircraftType}
                formatOutput={String}
                propertyMapping={
                    transportModeMapping?.data?.[planeType]
                        ? transportModeMapping.data[planeType].aircraftType
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill[planeType]?.aircraftType}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes[planeType]}
            />
        ),
        flightNumber: (
            <ParameterMapping<string>
                key={`${planeType}_flightNumber-parameter`}
                state={state}
                parameter={`${planeType}_flightNumber`}
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="direct"
                outputValues={PARAMETER_MAPPING_OUTPUT.flightNumber}
                formatOutput={String}
                propertyMapping={
                    transportModeMapping?.data?.[planeType]
                        ? transportModeMapping.data[planeType].flightNumber
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill[planeType]?.flightNumber}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes[planeType]}
            />
        ),
        departureOn: (
            <ParameterMapping<string>
                key={`${planeType}_departureOn-parameter`}
                state={state}
                parameter={`${planeType}_departureOn`}
                useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                mappingType="direct"
                outputValues={PARAMETER_MAPPING_OUTPUT.departureOn}
                formatOutput={String}
                propertyMapping={
                    transportModeMapping?.data?.[planeType]
                        ? transportModeMapping.data[planeType].departureOn
                        : undefined
                }
                onUpdate={updateTransportModeParameters}
                autoFill={autoFill[planeType]?.departureOn}
                legNumber={state.currentLeg}
                allowedRowIndexes={perTransportModeRowIndexes[planeType]}
            />
        ),
    })

    const electricTruckParameters = {
        refrigeration: buildRefrigerationParameter('Electric truck'),
    }

    const electricVanParameters = {
        refrigeration: buildRefrigerationParameter('Electric van'),
    }

    const emptyParameters = {}

    const parametersByGroup: {
        [G in TransportModeGroup]: {
            [K in keyof (typeof TRANSPORT_MODES_PER_GROUP_PROPERTY)[G]]: ReactNode
        }
    } = useMemo(() => {
        return {
            Sea: {},
            Rail: {},
            Air: {
                aircraftType: (
                    <GroupedParameterMapping<AircraftType, 'Air', 'aircraftType'>
                        state={state}
                        group={'Air'}
                        parameter="aircraftType"
                        mappingType="mapping"
                        outputValues={PARAMETER_MAPPING_OUTPUT.aircraftType}
                        formatOutput={String}
                        transportModeMapping={transportModeMapping}
                        onUpdate={updateGroupedTransportModeParameters}
                        autoFill={autoFill}
                        allowedRowIndexes={perTransportModeRowIndexes}
                    />
                ),
                flightNumber: (
                    <GroupedParameterMapping<RoadFuelType, 'Air', 'flightNumber'>
                        state={state}
                        group={'Air'}
                        parameter="flightNumber"
                        mappingType="direct"
                        outputValues={PARAMETER_MAPPING_OUTPUT.flightNumber}
                        formatOutput={String}
                        transportModeMapping={transportModeMapping}
                        onUpdate={updateGroupedTransportModeParameters}
                        autoFill={autoFill}
                        allowedRowIndexes={perTransportModeRowIndexes}
                    />
                ),
                departureOn: (
                    <GroupedParameterMapping<RoadFuelType, 'Air', 'departureOn'>
                        state={state}
                        group={'Air'}
                        parameter="departureOn"
                        mappingType="direct"
                        outputValues={PARAMETER_MAPPING_OUTPUT.departureOn}
                        formatOutput={String}
                        transportModeMapping={transportModeMapping}
                        onUpdate={updateGroupedTransportModeParameters}
                        autoFill={autoFill}
                        allowedRowIndexes={perTransportModeRowIndexes}
                    />
                ),
            },
            Road: {
                fuel: (
                    <GroupedParameterMapping<RoadFuelType, 'Road', 'fuel'>
                        state={state}
                        group={'Road'}
                        parameter="fuel"
                        mappingType="mapping"
                        outputValues={PARAMETER_MAPPING_OUTPUT.fuel}
                        formatOutput={getRoadFuelLabel}
                        transportModeMapping={transportModeMapping}
                        onUpdate={updateGroupedTransportModeParameters}
                        autoFill={autoFill}
                        allowedRowIndexes={perTransportModeRowIndexes}
                    />
                ),
                refrigeration: (
                    <GroupedParameterMapping<boolean, 'Road', 'refrigeration'>
                        state={state}
                        group={'Road'}
                        parameter="refrigeration"
                        mappingType="mapping"
                        outputValues={PARAMETER_MAPPING_OUTPUT.refrigeration}
                        formatOutput={String}
                        transportModeMapping={transportModeMapping}
                        onUpdate={updateGroupedTransportModeParameters}
                        autoFill={autoFill}
                        allowedRowIndexes={perTransportModeRowIndexes}
                    />
                ),
            },
            'Inland waterways': {},
        }
    }, [state, transportModeMapping, autoFill, perTransportModeRowIndexes])

    const parametersByMode: Record<TransportMode, { [k in TransportModeProperty]?: ReactNode }> =
        useMemo(() => {
            const simpleTransportModesRecord = simpleTransportModes.reduce(
                (acc, m) => {
                    acc[m] = emptyParameters
                    return acc
                },
                {} as Record<(typeof simpleTransportModes)[number], Record<string, ReactNode>>,
            )

            return {
                ...simpleTransportModesRecord,

                'Container Ship': containerShipParameters,

                'Cargo Plane': planeParameters('Cargo Plane'),
                'Passenger Plane': planeParameters('Passenger Plane'),
                Plane: planeParameters('Plane'),

                'Inferred truck': truckParameters('Inferred truck'),
                'Truck rigid 7.5t': truckParameters('Truck rigid 7.5t'),
                'Truck rigid 12t': truckParameters('Truck rigid 12t'),
                'Truck rigid 20t': truckParameters('Truck rigid 20t'),
                'Truck rigid 26t': truckParameters('Truck rigid 26t'),
                'Truck rigid 32t': truckParameters('Truck rigid 32t'),
                'Truck articulated 34t': truckParameters('Truck articulated 34t'),
                'Truck articulated 40t': truckParameters('Truck articulated 40t'),
                'Truck articulated 44t': truckParameters('Truck articulated 44t'),
                'Truck articulated 60t': truckParameters('Truck articulated 60t'),
                'Truck articulated 72t': truckParameters('Truck articulated 72t'),
                Truck: {
                    ...truckParameters('Truck'),
                    vehicleType: (
                        <ParameterMapping<RoadVehicleType>
                            key={'Truck_vehicleType-parameter'}
                            state={state}
                            parameter="Truck_vehicleType"
                            useTruncatedLabel={transportModeMapping?.mode?.source !== 'column'}
                            mappingType="mapping"
                            outputValues={PARAMETER_MAPPING_OUTPUT.vehicleType}
                            formatOutput={getRoadVehicleLabel}
                            propertyMapping={
                                transportModeMapping?.data?.Truck !== undefined
                                    ? transportModeMapping.data.Truck.vehicleType
                                    : undefined
                            }
                            onUpdate={updateTransportModeParameters}
                            autoFill={autoFill.Truck?.vehicleType}
                            legNumber={state.currentLeg}
                            allowedRowIndexes={perTransportModeRowIndexes.Truck}
                        />
                    ),
                },
                'Electric truck': electricTruckParameters,
                'Electric van': electricVanParameters,

                Rail: emptyParameters,
                'Electric train': emptyParameters,
                'Diesel train': emptyParameters,
                'Inland container vessel': emptyParameters,
            }
            // recreate when these core inputs change
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [state, transportModeMapping?.mode, transportModeMapping?.data, autoFill])

    const addPropertyField = (selection: DisplayProperty) => {
        setSelectedIds((prev) => (prev.includes(selection) ? prev : [...prev, selection]))
    }

    const options: DisplayProperty[] = useMemo(() => {
        const properties = propertiesToDisplayInMapping(transportModeMapping, parametersByMode)
        return cleanDisplayProperties(properties, transportModeMapping)
    }, [transportModeMapping])

    const autoSelectedIds = useMemo(() => {
        return options.filter((o) => {
            if (!transportModeMapping || !transportModeMapping.data) {
                return false
            }
            let transportMode: TransportMode
            if (o.type === 'transportMode') {
                transportMode = o.transportMode
            } else {
                assertType<'group'>(o.type)
                const transportModes = getGroupParameterTransportModes(o)
                if (!transportModes.length) {
                    return false
                }
                // Group properties are synced for all transport modes, just get the first
                transportMode = transportModes[0]
            }

            const transportModeProperties = transportModeMapping.data[transportMode]
            return (
                transportModeProperties &&
                objectHasKey(transportModeProperties, o.parameter) &&
                transportModeProperties[o.parameter]
            )
        })
    }, [options])

    const transportModeField =
        (transportModeMapping && !transportModeMapping.mode) ||
        transportModeMapping?.mode?.source === 'column' ? (
            <Box>
                <Labels />
                <ParameterMapping<TransportMode>
                    isTransportMode={true}
                    error={modeMappingError}
                    state={state}
                    parameter="transportMode"
                    mappingType="mapping"
                    outputValues={PARAMETER_MAPPING_OUTPUT.transportMode}
                    formatOutput={String}
                    propertyMapping={transportModeMapping.mode}
                    onUpdate={updateTransportModeParameters}
                    autoFill={autoFill.mode.transportMode}
                />
            </Box>
        ) : undefined

    // A helper function to maintain the properties order
    const sortFieldIds = (ids: DisplayProperty[]): DisplayProperty[] => {
        return ids.toSorted((a, b) => {
            const { type: typeA, parameter: prop1 } = a
            const { type: typeB, parameter: prop2 } = b

            // Present grouped parameters always first
            if (typeA !== typeB) {
                return typeA === 'group' ? 1 : -1
            } else {
                const modeA = a.type === 'group' ? a.group : a.transportMode
                const modeB = b.type === 'group' ? b.group : b.transportMode
                const modeComparison = modeA.localeCompare(modeB)
                return modeComparison !== 0 ? modeComparison : prop1.localeCompare(prop2)
            }
        })
    }

    const getRenderedProperties = () => {
        return uniqObj([...autoSelectedIds, ...selectedIds])
    }

    const renderedFields = useMemo(() => {
        const allIds = getRenderedProperties()
        const sortedIds = sortFieldIds(allIds)

        // Group fields by transport mode group
        const fieldsByGroup: Record<string, ReactNode[]> = {
            Sea: [],
            Air: [],
            Road: [],
            Rail: [],
            'Inland waterways': [],
        }

        // Transport mode field first (not grouped)
        const result: ReactNode[] = []
        if (transportModeField) {
            result.push(transportModeField)
        }

        sortedIds.forEach((id) => {
            const componentId = `${id.type}-${id.parameter}`
            const group =
                id.type === 'group' ? id.group : transportModeToGroupMapping[id.transportMode]
            if (id.type === 'group') {
                if (!(id.parameter in parametersByGroup[group])) {
                    throw new Error(`Display property shouldn't exist: ${group}-${id.parameter}`)
                }
                const property =
                    parametersByGroup[group][
                        id.parameter as keyof (typeof parametersByGroup)[typeof group]
                    ]
                fieldsByGroup[group].push(<Box key={componentId}>{property}</Box>)
            } else {
                const property = parametersByMode[id.transportMode][id.parameter]
                fieldsByGroup[group].push(<Box key={componentId}>{property}</Box>)
            }
        })

        Object.entries(fieldsByGroup).forEach(([group, fields]) => {
            if (fields.length > 0) {
                const groupIcon = groupIconMap[group as TransportModeGroup]
                result.push(
                    <Stack key={group} spacing={2} sx={{ mt: 4, mb: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            {EmissionEstimationIcons[groupIcon]}
                            <Text variant="h6">{group}</Text>
                        </Stack>
                        <Labels />
                        {fields}
                    </Stack>,
                )
            }
        })

        return result
    }, [transportModeField, selectedIds, autoSelectedIds, parametersByMode, selectedGroup])

    useEffect(() => {
        setSelectedIds([])
    }, [transportModeMapping?.mode])

    return (
        <>
            {renderedFields.length > 0 && <Box>{renderedFields}</Box>}
            {getRenderedProperties().length !== options.length && (
                <AdditionalPropertiesSelector options={options} onSelection={addPropertyField} />
            )}
        </>
    )
}

export default TransportModeParameters
